import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useUpdateUserProfileMutation } from "../api/userAuthApi";
import { AccountDetailsFormData } from "../helpers/userValidation";

export const useUpdateProfile = () => {
  const { t } = useTranslation();
  const [updateProfile, { isLoading }] = useUpdateUserProfileMutation();

  const handleUpdate = async (data: AccountDetailsFormData) => {
    const isPasswordChange = !!data.password_new;

    const loadingMessage = isPasswordChange
      ? t("feedback.user.updateProfile.passwordLoading", "Changing password...")
      : t("feedback.user.updateProfile.loading", "Saving changes...");

    const toastId = toast.loading(loadingMessage);

    try {
      await updateProfile(data).unwrap();

      const successMessage = isPasswordChange
        ? t(
            "feedback.user.updateProfile.passwordSuccess",
            "Password changed! You will be logged out."
          )
        : t(
            "feedback.user.updateProfile.success",
            "Account details changed successfully."
          );

      toast.success(successMessage, { id: toastId });

      if (isPasswordChange) {
        // Wait a moment for the user to read the toast, then reload the page.
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }

      return { success: true };
    } catch (err: any) {
      const errorCode = err?.data?.code;
      let errorMessage = t(
        "feedback.user.updateProfile.error",
        "Failed to update details. Please try again."
      );

      // Map both hyphenated and underscored versions for robustness.
      const errorMap: Record<string, string> = {
        incorrect_password: t(
          "feedback.user.updateProfile.incorrectPassword",
          "The current password you entered is incorrect."
        ),
        "incorrect-password": t(
          "feedback.user.updateProfile.incorrectPassword",
          "The current password you entered is incorrect."
        ),
        existing_user_email: t(
          "feedback.user.updateProfile.emailExists",
          "This email address is already in use."
        ),
        "existing-user-email": t(
          "feedback.user.updateProfile.emailExists",
          "This email address is already in use."
        ),
        invalid_email: t(
          "feedback.user.auth.invalidEmailFormat",
          "Please provide a valid email address."
        ),
        "invalid-email": t(
          "feedback.user.auth.invalidEmailFormat",
          "Please provide a valid email address."
        ),
        weak_password: t(
          "user.validation.weakPassword",
          "Password must be at least 8 characters long and include an uppercase letter and a number."
        ),
        "weak-password": t(
          "user.validation.weakPassword",
          "Password must be at least 8 characters long and include an uppercase letter and a number."
        ),
      };

      if (errorCode && errorMap[errorCode]) {
        errorMessage = errorMap[errorCode];
      }

      toast.error(errorMessage, { id: toastId });
      return { success: false, error: err };
    }
  };

  return { handleUpdate, isLoading };
};
